import { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from '../../../utility/axios-instance';
import Table from 'react-bootstrap/Table';
import { InputAdornment, TextField, } from "@mui/material";
import resetState from '../../../store/actions';
import { BsFillDashCircleFill } from "react-icons/bs";
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import Autocomplete from '@mui/material/Autocomplete';
import { Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MuiAlert from '@mui/material/Alert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FetchServicesApiResponse, Location, LocationsApiResponse, RoleMappingApiResponse, ServiceOption } from '@/types';
import SnackBar from '../../../components/SnackBar';

const CreateNewUser = () => {
	const [loading, setLoading] = useState(false);
	const [snackBar, setSnackBar] = useState({ display: false, type: "success", message: "default message!" })
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [showPassword, setShowPassword] = useState(false);
	const [states, setStates] = useState([]);
	const [locationCodes, setLocationCode] = useState<Location[]>([]);
	const [phoneNumber, setPhoneNumber] = useState('');
	const [locations, setLocations] = useState<Location[]>([]);
	const [userRole, setUserRole] = useState('');
	const [rolesOption, setRolesOption] = useState<string[]>([]);
	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const [newRole, setNewRole] = useState<string>('');
	const [servicesOption, setServicesOption] = useState<FetchServicesApiResponse["data"]>([]);
	const [newService, setNewService] = useState('');
	const [servicesAndPermissionsList, setServicesAndPermissionsList] = useState<ServiceOption[]>([]);

	// add permission to the services
	interface PermissionEvent extends React.ChangeEvent<HTMLInputElement> {
		target: HTMLInputElement & { name: string; checked: boolean };
	}

	const handlePermissionCheck = (event: PermissionEvent, service: string) => {
		const name = event.target.name;
		const isChecked = event.target.checked;

		let newServices = servicesAndPermissionsList.map((item) => {
			if (item.service === service) {
				isChecked && item.permissions.push(name);
				!isChecked && (item.permissions = item.permissions.filter((perm) => perm !== name));
			}
			return item;
		});
		setServicesAndPermissionsList(newServices);
	};

	const createRole = async (roleName:string) => {
		const payload = {
			roleName: roleName
		}
		await axios
			.post(`/roles/createRole`, payload)
			.then((res) => {
				const successMessage = res?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: successMessage, type: "success" }
				});
			})
			.catch((error) => {
				const errorMessage = error?.response?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
				});
			});
	};

	interface HandleAddRoleEvent extends React.FormEvent<HTMLFormElement> {}

	const handleAddRole = async (e: HandleAddRoleEvent): Promise<void> => {
		e.preventDefault();
		if (newRole !== '') {
			await createRole(newRole);
		}
		setNewRole('');
	};

	const createService = async (serviceName:string) => {
		const payload = {
			serviceName: serviceName
		}
		await axios
			.post(`/admin/createService`, payload)
			.then((res) => {
				const successMessage = res?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: successMessage, type: "success" }
				});
			})
			.catch((error) => {
				const errorMessage = error?.response?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
				});
			});
	};

	const fetchServices = async () => {
		await axios
			.get<FetchServicesApiResponse>(`/admin/fetchServices`)
			.then((res) => {
				const fetchedServices = res?.data?.data
				setServicesOption([...fetchedServices])
				return res.data
			})
			.catch((error) => {
				const errorMessage = error?.response?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
				});
			});
	};

	const fetchRoles = async () => {
		await axios
			.get('/roles/')
			.then((res) => {
				const fetchedRoles = res?.data?.data
				setRolesOption([...fetchedRoles]);
			})
			.catch((error) => {
				const errorMessage = error?.response?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
				});
			});
	};

	useEffect(() => {
		const fetchedServices = fetchServices()
		const fetchedRoles = fetchRoles()
	}, [newService, newRole])

	// after selecting role
	const handleSelectRole = (e: React.SyntheticEvent, value:string) => {
		let role = value;
		setUserRole(role);
		axios
			.get<RoleMappingApiResponse>(`/roles/${role}`)
			.then((res) => {
				const existingServicesAndPermissionsList = res?.data?.data?.permissions?.map((item) => {
					return {
						service: item?.name,
						permissions: item?.permissions
					}
				})
				setServicesAndPermissionsList(existingServicesAndPermissionsList)
			})
			.catch((err) => {
				const errorMessage = err?.response?.data?.message
				setSnackBar((prevSnackBar) => {
					return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
				})
			})

	}

	//add new services

	const handleAddService = async (e:any) => {
		e.preventDefault();

		if (newService !== '' && !servicesOption.includes(newService)) {
			let opts: ServiceOption = {
				service: newService,
				permissions: []
			};
			setServicesAndPermissionsList((prevServicesAndPermissionsList) => {
				return [...prevServicesAndPermissionsList, opts]
			});
			await createService(newService);
		}

		if (servicesOption.includes(newService)) {
			const errorMessage = `${newService} service already exist!`;
			setSnackBar((prevSnackBar) => {
				return {
					...prevSnackBar,
					display: true,
					message: errorMessage,
					type: "error",
				};
			});
		}

		setNewService('');
	};

	interface HandleSelectedServicesEvent extends React.SyntheticEvent {}

	const handleSelectedServices = (e: HandleSelectedServicesEvent, value: string): void => {
		let selectedService: string = value;
		let foundServiceRoleMapping: ServiceOption | undefined = servicesAndPermissionsList.find(
			(serviceRoleMapping) => serviceRoleMapping.service === selectedService
		);
		let opts: ServiceOption = {
			service: selectedService,
			permissions: []
		};
		!foundServiceRoleMapping &&
			setServicesAndPermissionsList((prevServicesAndPermissionsList) => {
				return [...prevServicesAndPermissionsList, opts];
			});
	};

	const handleRemoveService = (service:string) => {
		let newServices = servicesAndPermissionsList.filter(item => item.service !== service);
		setServicesAndPermissionsList(newServices);
	}
	// onSubmit handler

	const createUser = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoading(true);
		const formData = new FormData(e.target as HTMLFormElement);
		const username = formData.get('username') as string;
		const name = formData.get('name') as string;
		const role = formData.get('role') as string;
		const password = formData.get('password') as string;
		const phone = formData.get('phone') as string;
		const location = formData.get('location') as string;
		let locationCodes = locations.map(item => item.locationCode)
		let checkAnyPermissionIsEmpty = servicesAndPermissionsList.find(item => item.permissions.length === 0);
		if (
			checkAnyPermissionIsEmpty || (servicesAndPermissionsList.length === 0 && !['admin', 'super-admin'].includes(role) && role !== '')
		) {
			const errorMessage = "Permission empty not allowed, Please select anyone"
			setSnackBar((prevSnackBar) => {
				return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
			});
			setLoading(false)
		}
		else {
			axios
				.post('/admin/new', {
					username: username,
					name: name,
					role: role,
					phone: `+91${phone}`,
					services: servicesAndPermissionsList,
					password: password,
					defaultLocation: location,
					locationCodes: locationCodes.length !== 0 ? locationCodes : undefined,
				})
				.then((res) => {
					const successMessage = 'User created successfully'
					setSnackBar((prevSnackBar) => {
						return { ...prevSnackBar, display: true, message: successMessage, type: "success" }
					});
					setTimeout(() => {
						navigate(-1);
					}, 1000);
				})
				.catch((err) => {
					const errorMessage = err?.response?.data?.message
					setSnackBar((prevSnackBar) => {
						return { ...prevSnackBar, display: true, message: errorMessage, type: "error" }
					});
					if (err.response.status === 401) {
						setTimeout(() => {
							resetState(dispatch);
						}, 1000);
					}
				})
				.finally(() => {
					setLoading(false);
				})
		}
	};

	useEffect(() => {
		axios
			.get('/pincode/getAllStates')
			.then((response) => {
				setStates(response?.data?.data)
			})

		axios
			.get<LocationsApiResponse>(`/location/locations`)
			.then((response) => {
				setLocationCode(response?.data?.data)
			})
	}, [])

	const handleNumChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const limit: number = 10;
		setPhoneNumber(event.target.value.slice(0, limit));
	};

	const handleMinLength = (
		event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
	): void => {
		if (event.target.value) {
		const phoneno: RegExp = /^\d{10}$/;
		if (!event.target.value.match(phoneno)) {
			alert("Please enter 10 digit phone number");
			setPhoneNumber("");
		}
		}
	};

	return (
		<div 
			className="d-flex flex-column" 
			style={{marginTop:10,borderRadius:8,boxShadow: "0 0 0.3152rem rgba(0,0,0,.181), 0 0 0.3152rem rgba(0,0,0,.181)",padding:20}}
		>
			<p style={{ background: "linear-gradient(to right, #ff6200, white)",paddingLeft: 20,borderRadius: 26,fontSize: 15,padding: "5px 15px",color: "#fff",margin:0,marginTop:20 }}>Create New User</p>
			<div className="d-flex justify-content-center align-items-center flex-grow-1 mt-3">
				<form
					className="d-flex flex-column"
					style={{ gridGap: '1rem', width: '100%',marginTop:10 }}
					onSubmit={createUser}
				>
					<SnackBar snackBar={snackBar} setSnackBar={setSnackBar} />
					<div style={{ display: 'flex', gap: '5px' }} >
						<TextField
							id="username"
							required
							name="username"
							type="text"
							label="Username"
							placeholder='Enter Username'
							autoComplete='off'
							size='small'
							sx={{ width: '50%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PersonIcon />
									</InputAdornment>
								),
							}}
						/>
						<TextField
							required
							id="name"
							name="name"
							type="text"
							label="Name"
							placeholder='Enter Name'
							size='small'
							autoComplete='off'
							sx={{ width: '50%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
						/>
					</div>

					<div style={{ display: 'flex', gap: '5px',marginTop:10 }} >
						<Autocomplete
							disablePortal
							disableClearable
							id="location"
							options={states}
							sx={{ width: '50%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							renderInput={(params) => <TextField name="location" {...params} label="State" />}
						/>
						
						<Autocomplete
							disablePortal
							disableClearable
							id="locationCode" 
							multiple
							onChange={(e, value) => setLocations(value)}
							options={locationCodes}
							getOptionLabel={(location) => `${location.locationCode} - ${location.locationName}`}
							sx={{ width: '50%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							renderInput={(params) => <TextField name="locationCode" {...params} label="Location Code" />}
						/>

					</div>

					<div style={{ display: 'flex', gap: '10px',marginTop:10 }}>
						<Autocomplete
							disablePortal
							disableClearable
							id="role" 
							onChange={handleSelectRole}
							options={rolesOption}
							sx={{ width: '65%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							renderInput={(params) => <TextField name="role" {...params} label="Role" required />}
						/>
						<div style={{ display: 'flex', width: '35%' }}>
							<TextField
								sx={{ width: '100%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
								size='small'
								label="Add New Role" type="text" value={newRole} onChange={(e) => setNewRole(e.target.value)} InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={(e) => { e.preventDefault(); handleAddRole(e as unknown as HandleAddRoleEvent); }} >
												<AddCircleIcon />
											</IconButton>
										</InputAdornment>
									),
								}} />
						</div>
					</div>

					<div style={{ display: 'flex', gap: '10px',marginTop:10 }}>
						<Autocomplete
							disablePortal
							disableClearable
							id="services" 
							onChange={handleSelectedServices}
							options={servicesOption.filter((item) => { if (item) return item; return false })}
							sx={{ width: '65%', '& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							renderInput={(params) => <TextField name="services" {...params} label="Services"  />}
						/>
						<div style={{ display: 'flex', width: '35%' }}>
							<TextField label="Add New Service" type="text"
								sx={{ width: '100%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
								size='small'
								value={newService} onChange={(e) => setNewService(e.target.value)} InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={handleAddService} >
												<AddCircleIcon />
											</IconButton>
										</InputAdornment>
									),
								}} />
						</div>
					</div>

					{servicesAndPermissionsList.length !== 0 ?
						<Table striped bordered hover>
							<thead style={{ backgroundColor: '#243750', color: 'white', fontSize: '16px' }}>
								<tr>
									<th>Services</th>
									<th>Permissions</th>
									<th>Remove</th>
								</tr>
							</thead>
							<tbody>
								{servicesAndPermissionsList.map((serviceAndPermission, index) => (
									<tr key={index}>
										<td>{serviceAndPermission?.service?.toUpperCase()}</td>
										<td>
											<label style={{ marginRight: 25 }}>
												<input
													style={space}
													type="checkbox"
													name="create"
													checked={serviceAndPermission.permissions.includes("create")}
													onChange={(e) => handlePermissionCheck(e, serviceAndPermission?.service)}
												/>
												Create
											</label>
											<label style={{ marginRight: 25 }}>
												<input
													style={space}
													type="checkbox"
													name="read"
													checked={serviceAndPermission.permissions.includes("read")}
													onChange={(e) => handlePermissionCheck(e, serviceAndPermission?.service)}
												/>
												Read
											</label>
											<label style={{ marginRight: 25 }}>
												<input
													style={space}
													type="checkbox"
													name="update"
													checked={serviceAndPermission.permissions.includes("update")}
													onChange={(e) => handlePermissionCheck(e, serviceAndPermission?.service)}
												/>
												Update
											</label>
											<label style={{ marginRight: 25 }}>
												<input
													style={space}
													type="checkbox"
													name="delete"
													checked={serviceAndPermission.permissions.includes("delete")}
													onChange={(e) => handlePermissionCheck(e, serviceAndPermission?.service)}
												/>
												Delete
											</label>
										</td>
										<td><BsFillDashCircleFill
											style={{ color: 'red', cursor: 'pointer' }}
											onClick={() => handleRemoveService(serviceAndPermission?.service)}
										/></td>
									</tr>
								))}
							</tbody>
						</Table>
						: null}

					<div style={{ display: 'flex', width: '100%',marginTop:10 }} >
						<TextField
							sx={{ width: '100%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							label="Password"
							type={showPassword ? 'text' : 'password'}
							id="password"
							name='password'
							placeholder="Password"
							autoComplete='off'
							required
							inputProps={{ minLength: 6 }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={handleClickShowPassword}>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}} />
					</div>

					<p style={{ background: "linear-gradient(to right, #ff6200, white)",paddingLeft: 20,borderRadius: 26,fontSize: 15,padding: "5px 15px",color: "#fff",margin:0,marginTop:20 }}>Contact</p>

					<div style={{ display: 'flex', width: '100%',marginTop:10 }}>
						<TextField 
							required
							id='phoneNumber'
							name="phone"
							type="number"
							label="Phone Number"
							inputProps={{ maxLength: 10 }}
							onBlur={(e) => handleMinLength(e)}
							onChange={handleNumChange}
							placeholder='Enter Your 10 Digit Phone Number'
							value={phoneNumber ? phoneNumber : ''}
							autoComplete='off'
							sx={{ width: '50%','& .MuiOutlinedInput-root': {borderRadius:'26px'} }}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PhoneIcon />
									</InputAdornment>
								)
							}}
						/>
					</div>
					<div style={{ display: 'flex',margin: '0px 5px',justifyContent:'flex-end',marginTop:20 }}>
						<Button
							type="submit"
							variant="contained"
							style={{ backgroundColor: '#ff6200' }}
							// sx={{ width: '50%' }}
							// startIcon={<PublishIcon />}
						>
							{loading ? (
								<span
									className="spinner-border spinner-border-sm"
									role="status"
									aria-hidden="true"
								></span>
							) : (
								'Submit'
							)}
						</Button>
					</div>
				</form>
			</div>

		</div>
	);
};

export default CreateNewUser;


const space = {
	margin: 'auto 5px'
}
const serStyle = {
	padding: '5px 10px',
	margin: '5px',
	backgroundColor: '#ff6200',
	borderRadius: '8px'
}

